import "./instrument";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import App from "./App";
import "./index.css";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";

const rootEl = document.getElementById("root")!;
const root = createRoot(rootEl, {
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn("Uncaught error", error, errorInfo.componentStack);
  }),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
});
root.render(<App />);

// ─── Last-resort crash recovery ───────────────────────────────────────────────
// If an uncaught error reaches above our ErrorBoundary (e.g. from a provider),
// React empties the #root element and stops. Nothing inside React can observe
// this, so we watch from outside using a MutationObserver.
//
// Uses sessionStorage to count reloads and stop at MAX_RELOADS to prevent
// an infinite loop when the crash is persistent rather than transient.
const RELOAD_COUNT_KEY = 'gf_crash_reloads';
// Marks that the current page load was triggered by a crash recovery reload.
// Prevents armCrashWatchdog from resetting the counter on crash-induced reloads.
const CRASH_RELOAD_FLAG = 'gf_crash_reload_pending';
const MAX_RELOADS = 3;

// Watches for the root becoming empty AFTER it has successfully mounted.
// Triggered by armCrashWatchdog() once the initial mount is confirmed.
const crashObserver = new MutationObserver(() => {
  if (rootEl.childNodes.length > 0) return;

  crashObserver.disconnect();

  const reloads = Number(sessionStorage.getItem(RELOAD_COUNT_KEY) || 0);

  if (reloads >= MAX_RELOADS) {
    // Persistent crash — stop looping and show a static recovery screen.
    // Uses Tailwind classes + CSS variables so it automatically respects the
    // active light/dark theme (the .dark class is still on <html>).
    rootEl.innerHTML = `
      <div class="min-h-screen flex items-center justify-center p-8 bg-background">
        <div class="text-center space-y-4 max-w-sm">
          <h2 class="text-lg font-semibold text-foreground">Something went wrong</h2>
          <p class="text-sm text-muted-foreground">
            The application encountered an unexpected error. Please refresh or clear your browser data.
          </p>
          <button
            onclick="sessionStorage.removeItem('${RELOAD_COUNT_KEY}');sessionStorage.removeItem('${CRASH_RELOAD_FLAG}');window.location.reload()"
            class="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 transition-opacity cursor-pointer border-0"
          >
            Try Again
          </button>
        </div>
      </div>`;
    return;
  }

  sessionStorage.setItem(RELOAD_COUNT_KEY, String(reloads + 1));
  sessionStorage.setItem(CRASH_RELOAD_FLAG, 'true');
  window.location.reload();
});

function armCrashWatchdog() {
  // If this load was triggered by a crash recovery reload, preserve the counter
  // so it can accumulate toward MAX_RELOADS. Only clear it on fresh (non-crash) loads.
  const isCrashReload = sessionStorage.getItem(CRASH_RELOAD_FLAG) === 'true';
  sessionStorage.removeItem(CRASH_RELOAD_FLAG);
  if (!isCrashReload) {
    sessionStorage.removeItem(RELOAD_COUNT_KEY);
  }
  crashObserver.observe(rootEl, { childList: true });
}

// Wait for the initial React render to complete before arming the crash watchdog.
// React 18 renders asynchronously — a single rAF is NOT sufficient. Instead we
// use a MutationObserver to detect the exact moment #root first gets children,
// then hand off to the crash watchdog. Purely event-driven, no timing assumptions.
const mountObserver = new MutationObserver(() => {
  if (rootEl.childNodes.length === 0) return;
  mountObserver.disconnect();
  armCrashWatchdog();
});
mountObserver.observe(rootEl, { childList: true });
